import type { ApiClient } from '../client.js';
import { ENTITY_DIRS, type ConfigDiff, type BaseConfig, type BaseMeta, type EntityKind } from './types.js';

export interface BaseRecord {
  id: string;
  environment?: string;
  integrations?: string;
  origin_base_id?: string | null;
  org_id?: string;
  name?: string;
  description?: string;
}

/** Map a server `BaseRecord` to the on-disk `BaseMeta`, falling back to `fallbackId` for the id. */
export function metaFromRecord(record: BaseRecord, fallbackId: string): BaseMeta {
  return {
    base_id: record.id ?? fallbackId,
    origin_base_id: record.origin_base_id ?? undefined,
    name: record.name,
    description: record.description,
    environment: record.environment,
    integrations: record.integrations,
  };
}

export interface ApplyResult {
  diff: ConfigDiff;
  new_trigger_secrets: Record<string, string>;
  new_inbound_webhook_secrets: Record<string, string>;
}

export function getBaseRecord(client: ApiClient, id: string): Promise<BaseRecord> {
  return client.request<BaseRecord>('GET', `/v1/bases/${id}`);
}

export function getConfig(client: ApiClient, id: string): Promise<BaseConfig> {
  return client.request<BaseConfig>('GET', `/v1/${id}/config`);
}

export function diffConfig(client: ApiClient, id: string, local: BaseConfig): Promise<ConfigDiff> {
  return client.request<ConfigDiff>('POST', `/v1/${id}/config/diff`, local);
}

export function applyConfig(client: ApiClient, id: string, local: BaseConfig): Promise<ApplyResult> {
  return client.request<ApplyResult>('PUT', `/v1/${id}/config`, local);
}

export function createPreview(client: ApiClient, originId: string, name: string, description?: string): Promise<BaseRecord> {
  return client.request<BaseRecord>('POST', `/v1/${originId}/preview`, {
    name,
    ...(description ? { description } : {}),
  });
}

/** Delete one config entity via its per-entity route (used by `push --prune`). */
export function deleteEntity(client: ApiClient, baseId: string, kind: EntityKind, id: string): Promise<unknown> {
  return client.request('DELETE', `/v1/${baseId}/${ENTITY_DIRS[kind]}/${id}`);
}
